/**
 * /devhub/runtime-control — Area A2 (Runtime Control). REQ-001.91 DevHub UI rebuild · backend on/off.
 *
 * The operator's premium control surface for the two local IDL services — the grounding `guardrail`
 * (idl-localserver, 127.0.0.1:8787) and the execution `worker` (rag_chat_worker). For each service it
 * shows desired-vs-observed status (icon + Tag + text), an On/Off Toggle wired to the local control
 * bridge, the `requires` prerequisite list when a service is blocked, an "auto-off after N minutes"
 * timer with a live countdown, and a maintenance banner while a service is observed-down.
 *
 * HONEST boundary (the browser NEVER runs shell — it only calls the operator's local control bridge):
 *   • Bridge reachable (operator's machine): full controls, live status, timer auto-off.
 *   • Bridge unreachable (DEPLOYED origin): every control is DISABLED with the exact reason
 *     ("requires the local runtime bridge"), never hidden. State is read-only and clearly framed.
 *
 * Render-loop-safe: no new-ref store selectors; the countdown uses one setInterval cleaned up on
 * unmount with stable deps; the auto-off fires through a ref so the effect never re-subscribes.
 * Carbon-only, g100, zero inline styles (Carbon tokens via existing `.svc-control` / `.cockpit-*`
 * classes + Carbon `Grid`/`Stack`); status is icon + text (never colour alone) for WCAG.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import {
  InlineNotification,
  RadioButton,
  RadioButtonGroup,
  Stack,
  Tag,
  Tile,
  Toggle,
} from '@carbon/react';
import {
  CheckmarkFilled,
  CloudOffline,
  PauseFilled,
  Time,
  WarningAltFilled,
} from '@carbon/icons-react';

import { DevHubPanelPage } from '../components/devhub/DevHubPanelPage';
import {
  useServiceControl,
  type ServiceName,
  type ServiceStatus,
} from '@lib/persistence/useServiceControl';

export const Route = createFileRoute('/devhub/runtime-control')({ component: RuntimeControlPage });

/** Timer presets the operator can pick when turning a service on. `manual` ⇒ no auto-off. */
type TimerPreset = '15' | '30' | '60' | 'manual';
const TIMER_PRESETS: ReadonlyArray<{ value: TimerPreset; label: string }> = [
  { value: '15', label: '15 min' },
  { value: '30', label: '30 min' },
  { value: '60', label: '60 min' },
  { value: 'manual', label: 'Manual (stay on)' },
];

/** mm:ss for the live countdown. */
function formatRemaining(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function RuntimeControlPage() {
  const { services, bridgeReachable, isLoading, act } = useServiceControl();

  return (
    <DevHubPanelPage
      title="Runtime control"
      subtitle="Turn the local guardrail + execution worker on/off through the local runtime bridge — with an auto-off timer. The browser never runs shell; it only calls the operator's local bridge."
      status={{
        label: bridgeReachable ? 'bridge live' : 'bridge offline',
        live: bridgeReachable,
      }}
    >
      <div className="view-stack">
        {!bridgeReachable && !isLoading ? (
          <InlineNotification
            kind="info"
            lowContrast
            hideCloseButton
            role="status"
            title="Local runtime bridge required"
            subtitle="This deployed surface cannot reach 127.0.0.1 / launchd. On/Off controls are disabled here and shown read-only — start the on-machine control bridge (GET /__local/service/*, wrapping scripts/service_control.sh) to drive the guardrail and worker. The browser never runs shell."
          />
        ) : null}

        <section className="cockpit-section">
          <h2 className="cockpit-section__title">Local services</h2>
          <div className="cockpit-services">
            {SERVICE_DEFS.map((def) => {
              const live = services.find((s) => s.name === def.name) ?? null;
              return (
                <ServiceControlCard
                  key={def.name}
                  def={def}
                  service={live}
                  bridgeReachable={bridgeReachable}
                  isLoading={isLoading}
                  act={act}
                />
              );
            })}
          </div>
        </section>

        <p className="view-tile__label">
          Source of truth: scripts/service_control.sh (operator Turn On / Turn Off). Status is read via
          GET /__local/service/status; actions via GET /__local/service/&lt;guardrail|worker&gt;/&lt;on|off&gt;.
          The timer&apos;s auto-off calls the same off endpoint — there is no server-side scheduler, so the
          countdown only runs while this panel is open.
        </p>
      </div>
    </DevHubPanelPage>
  );
}

/** Static framing per service (label/endpoint fall back to these if the bridge omits them). */
interface ServiceDef {
  name: ServiceName;
  label: string;
  endpoint?: string;
  /** Plain-language note shown under the title. */
  blurb: string;
  /** The maintenance copy shown while this service is observed-down. */
  maintenance: string;
}

const SERVICE_DEFS: ReadonlyArray<ServiceDef> = [
  {
    name: 'guardrail',
    label: 'Grounding guardrail',
    endpoint: '127.0.0.1:8787',
    blurb: 'idl-localserver — the grounding seam every answer must pass.',
    maintenance:
      'Backend in maintenance — the grounding guardrail is paused; requests cannot be grounded and are held until it resumes.',
  },
  {
    name: 'worker',
    label: 'Execution worker',
    blurb: 'rag_chat_worker — drains action frames and writes results back.',
    maintenance:
      'Backend in maintenance — the execution worker is paused; new requests are queued and drain when it resumes.',
  },
];

interface ServiceControlCardProps {
  def: ServiceDef;
  service: ServiceStatus | null;
  bridgeReachable: boolean;
  isLoading: boolean;
  act: (name: ServiceName, action: 'on' | 'off') => Promise<void>;
}

function ServiceControlCard({
  def,
  service,
  bridgeReachable,
  isLoading,
  act,
}: ServiceControlCardProps) {
  const observed = service?.observed ?? 'down';
  const desired = service?.desired ?? observed;
  const observedUp = observed === 'up';
  const requires = service?.requires ?? [];
  const controllable = (service?.controllable ?? true) && bridgeReachable;
  // A prerequisite only BLOCKS turning-on while the service is down (it explains why it can't come up).
  const blocked = requires.length > 0 && !observedUp;
  const drift = desired !== observed;

  // --- Timer state (per card) ------------------------------------------------------------------
  const [preset, setPreset] = useState<TimerPreset>('30');
  const [deadline, setDeadline] = useState<number | null>(null);
  const [remaining, setRemaining] = useState<number>(0);
  const [busy, setBusy] = useState<boolean>(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // The auto-off action goes through a ref so the countdown effect never re-subscribes on act change.
  const fireOffRef = useRef<() => void>(() => {});

  const runAction = useCallback(
    async (action: 'on' | 'off') => {
      setActionError(null);
      setBusy(true);
      try {
        await act(def.name, action);
      } catch (err) {
        setActionError((err as Error).message ?? 'bridge action failed');
      } finally {
        setBusy(false);
      }
    },
    [act, def.name]
  );

  // Stable auto-off used by both the timer and the ref.
  const autoOff = useCallback(() => {
    setDeadline(null);
    setRemaining(0);
    void runAction('off');
  }, [runAction]);
  fireOffRef.current = autoOff;

  const handleToggle = useCallback(
    (checked: boolean) => {
      if (checked) {
        // Turning ON: arm the timer unless the operator chose Manual (stay on).
        const minutes = preset === '15' ? 15 : preset === '30' ? 30 : preset === '60' ? 60 : 0;
        if (minutes > 0) {
          const ms = minutes * 60_000;
          setDeadline(Date.now() + ms);
          setRemaining(ms);
        } else {
          setDeadline(null);
          setRemaining(0);
        }
        void runAction('on');
      } else {
        // Turning OFF manually: clear any running timer.
        setDeadline(null);
        setRemaining(0);
        void runAction('off');
      }
    },
    [preset, runAction]
  );

  // Live countdown — one interval, cleaned up on unmount / when the deadline changes. Stable deps.
  useEffect(() => {
    if (deadline == null) return;
    const tick = () => {
      const left = deadline - Date.now();
      if (left <= 0) {
        fireOffRef.current();
        return;
      }
      setRemaining(left);
    };
    tick();
    const id = window.setInterval(tick, 1_000);
    return () => window.clearInterval(id);
  }, [deadline]);

  // If the service is observed-down (crashed / stopped out-of-band), a running timer is moot.
  useEffect(() => {
    if (!observedUp && deadline != null) {
      setDeadline(null);
      setRemaining(0);
    }
  }, [observedUp, deadline]);

  const timerActive = deadline != null && observedUp;
  const toggleId = `runtime-toggle-${def.name}`;

  return (
    <Tile className="svc-control">
      <Stack gap={5}>
        {/* Header: label + endpoint + the On/Off toggle. */}
        <div className="svc-control__row">
          <div className="svc-control__meta">
            <p className="svc-control__label">{service?.label ?? def.label}</p>
            {(service?.endpoint ?? def.endpoint) ? (
              <p className="svc-control__endpoint">{service?.endpoint ?? def.endpoint}</p>
            ) : null}
            <p className="svc-control__endpoint">{def.blurb}</p>
          </div>
          <Toggle
            id={toggleId}
            size="sm"
            labelA="Off"
            labelB="On"
            toggled={observedUp}
            disabled={!controllable || busy || isLoading || (blocked && !observedUp)}
            onToggle={handleToggle}
            aria-label={`Turn ${service?.label ?? def.label} ${observedUp ? 'off' : 'on'}`}
          />
        </div>

        {/* Desired-vs-observed status — icon + Tag + text (never colour alone). */}
        <div className="notification-row__meta">
          <StatusPill observed={observed} />
          {drift ? (
            <Tag type="magenta" size="sm" renderIcon={WarningAltFilled}>
              desired {desired} · observed {observed}
            </Tag>
          ) : null}
        </div>

        {/* Timer controls: choose a duration, then a live countdown while armed. */}
        <Stack gap={4}>
          <RadioButtonGroup
            legendText="Auto-off after"
            name={`runtime-timer-${def.name}`}
            valueSelected={preset}
            orientation="horizontal"
            disabled={!controllable}
            onChange={(value) => setPreset(value as TimerPreset)}
          >
            {TIMER_PRESETS.map((p) => (
              <RadioButton
                key={p.value}
                id={`runtime-timer-${def.name}-${p.value}`}
                labelText={p.label}
                value={p.value}
              />
            ))}
          </RadioButtonGroup>

          {preset === 'manual' ? (
            <p className="svc-control__endpoint">
              Manual — the service stays on until you turn it off here.
            </p>
          ) : null}

          {timerActive ? (
            <Tag type="blue" size="md" renderIcon={Time}>
              Auto-off in {formatRemaining(remaining)}
            </Tag>
          ) : null}
        </Stack>

        {/* Maintenance banner while the service is OFF (observed down). */}
        {!observedUp ? (
          <InlineNotification
            kind="warning"
            lowContrast
            hideCloseButton
            role="status"
            title="Backend in maintenance"
            subtitle={def.maintenance}
          />
        ) : null}

        {/* Prerequisite block — the exact `requires` list, named, never a fake control path. */}
        {blocked ? (
          <InlineNotification
            kind="info"
            lowContrast
            hideCloseButton
            role="status"
            title="Prerequisite required (deny-by-default)"
            subtitle={`This service refuses to start until its prerequisite exists locally: ${requires.join('; ')}.`}
          />
        ) : null}

        {/* Honest disabled reason when the control bridge is unreachable (deployed). */}
        {!bridgeReachable ? (
          <p className="svc-control__note">
            <CloudOffline size={16} aria-hidden="true" /> Disabled — requires the local runtime bridge.
            The deployed origin cannot reach 127.0.0.1 / launchd; status is read-only here and actions
            never run from the cloud edge.
          </p>
        ) : null}

        {actionError ? (
          <InlineNotification
            kind="error"
            lowContrast
            role="alert"
            title="Bridge action failed"
            subtitle={actionError}
            onCloseButtonClick={() => setActionError(null)}
          />
        ) : null}
      </Stack>
    </Tile>
  );
}

/** Observed-state pill: icon + Tag + text (WCAG — never colour alone). */
function StatusPill({ observed }: { observed: 'up' | 'down' }) {
  if (observed === 'up') {
    return (
      <Tag type="green" size="md" renderIcon={CheckmarkFilled}>
        Running (observed up)
      </Tag>
    );
  }
  return (
    <Tag type="cool-gray" size="md" renderIcon={PauseFilled}>
      Stopped (observed down)
    </Tag>
  );
}
